import { useRef, useState } from 'react';
import {
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  FAIconName,
  FireflyButton,
  Icon,
  ModalV2,
  Text,
  ToastMessage,
} from '@equip.health/ui';
import {
  ACCEPTED_IMAGE_TYPES,
  CIRCLE_DIAMETER,
  GENERIC_API_FAILURE_MESSAGE,
  INITIAL_POSITION,
  INITIAL_SCALE,
  UPLOAD_STATES,
} from '~/lib/constants/profile.constants';
import { validateFile } from '~/lib/util/profile.util';
import AvatarImageEditor from './AvatarImageEditor';
import { useEditProviderAvatarByIdMutation } from '~/store/services/providers';

export interface AvatarUploadProps {
  isOpen: boolean;
  onClose: () => void;
  providerExternalId: string;
}

const TOP_LEVEL_ID = 'avatar-upload';

const AvatarUpload = ({
  isOpen,
  onClose,
  providerExternalId,
}: AvatarUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState(UPLOAD_STATES.INITIAL);
  const [uploadError, setUploadError] = useState('');
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [position, setPosition] = useState(INITIAL_POSITION);
  const [processedFile, setProcessedFile] = useState<File | null>(null);

  const [editProviderAvatar, editProviderAvatarResult] =
    useEditProviderAvatarByIdMutation();

  const resetStates = () => {
    setFile(null);
    setCroppedImageUrl(null);
    setUploadState(UPLOAD_STATES.INITIAL);
    setScale(INITIAL_SCALE);
    setPosition(INITIAL_POSITION);
    setUploadError('');
    setProcessedFile(null);
  };

  const validateAndUploadFile = (uploadedFile: File) => {
    const errorMessage = validateFile(uploadedFile, false);
    if (errorMessage) {
      setUploadError(errorMessage);
    } else {
      setUploadError('');
      setFile(uploadedFile);
      setUploadState(UPLOAD_STATES.EDIT);
    }
  };

  const onDrag = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setUploadError('');
    setIsDragging(event.type !== 'dragleave' && !file);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setUploadError('');
    setIsDragging(false);
    const droppedFile = event?.dataTransfer?.files[0];
    if (droppedFile) validateAndUploadFile(droppedFile);
  };

  const onUpload = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    editProviderAvatarResult.reset();
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      const cacheBustingParameter = `?${Date.now()}`;
      const fileInput = event.target;
      fileInput.value = '';
      fileInput.files = null;
      fileInput.setAttribute(
        'data-cache-busting-parameter',
        cacheBustingParameter,
      );
      validateAndUploadFile(uploadedFile);
    }
  };

  const renderToastMessage = (id: string, text: string) => (
    <ToastMessage
      icon={FAIconName.exclamationTriangleRegular}
      id={`${TOP_LEVEL_ID}__${id}`}
      isFullWidth
      text={text}
      variant="critical"
    />
  );

  const createFileFromUrl = async (croppedURL: string) => {
    try {
      const res = await fetch(croppedURL);
      const blob = await res.blob();
      const imageFile = new File([blob], file!.name, {
        type: file!.type,
      });
      setProcessedFile(imageFile);
      return imageFile;
    } catch (error) {
      console.error('Error creating file from URL');
      setProcessedFile(null);
      return null;
    }
  };

  const handleSave = async () => {
    try {
      if (!processedFile) return;
      const formData = new FormData();
      formData.append('file', processedFile);
      await editProviderAvatar({
        editData: formData,
        providerExternalId,
      }).unwrap();
      resetStates();
      onClose();
    } catch (error) {
      editProviderAvatarResult.isError = true;
    }
  };

  const handlePreview = async (croppedImageUrl: string) => {
    setCroppedImageUrl(croppedImageUrl);
    const newProcessedFileFromUrl: File =
      await createFileFromUrl(croppedImageUrl);
    if (newProcessedFileFromUrl) {
      const errorMessage = validateFile(newProcessedFileFromUrl, true);
      if (errorMessage) {
        setUploadError(errorMessage);
      } else {
        setUploadError('');
      }
    }
    setUploadState(UPLOAD_STATES.PREVIEW);
  };

  const InitialContent = () => (
    <>
      <ChakraBox
        alignContent="center"
        background={isDragging ? 'secondary.5' : 'neutral.background.primary'}
        border="1px dashed"
        borderColor="neutral.line"
        borderRadius="12px"
        id={`${TOP_LEVEL_ID}__box`}
        justifyItems="center"
        marginBottom="18px"
        marginTop="12px"
        minHeight="200px"
        onDragEnter={onDrag}
        onDragLeave={onDrag}
        onDragOver={onDrag}
        onDrop={onDrop}
        padding="18px"
      >
        <ChakraFlex alignItems="center" gap="4px">
          <Icon color="primary.100" name={FAIconName.folderArrowUp} size="lg" />
          <Text color="neutral.secondary" variant="bodyMedium">
            Drop image here or
          </Text>
          <Text
            color="primary.100"
            cursor="pointer"
            onClick={onUpload}
            variant="bodyMedium"
          >
            click to upload
          </Text>
        </ChakraFlex>
      </ChakraBox>
      {uploadError && renderToastMessage('warning', uploadError)}
    </>
  );

  const renderContent = () => {
    switch (uploadState) {
      case UPLOAD_STATES.PREVIEW:
        return (
          <>
            <ChakraFlex justifyContent="center" padding="30px 0 48px 0">
              <ChakraImage
                alt="Preview"
                borderRadius="50%"
                height={`${CIRCLE_DIAMETER}px`}
                src={croppedImageUrl || ''}
                width={`${CIRCLE_DIAMETER}px`}
              />
            </ChakraFlex>
            {uploadError && renderToastMessage('warning', uploadError)}
            {editProviderAvatarResult.isError &&
              renderToastMessage('error', GENERIC_API_FAILURE_MESSAGE)}
            <ChakraFlex
              alignItems="center"
              justifyContent="space-between"
              marginTop="10px"
              width="100%"
            >
              <FireflyButton
                id={`${TOP_LEVEL_ID}__left-button`}
                leftIcon={FAIconName.arrowLeftRegular}
                onClick={() => setUploadState(UPLOAD_STATES.EDIT)}
                variant="secondary"
              >
                Back
              </FireflyButton>
              <FireflyButton
                id={`${TOP_LEVEL_ID}__right-button`}
                isDisabled={uploadError !== ''}
                isLoading={editProviderAvatarResult.isLoading}
                leftIcon={FAIconName.checkRegular}
                onClick={handleSave}
                variant="primary"
              >
                Save
              </FireflyButton>
            </ChakraFlex>
          </>
        );
      case UPLOAD_STATES.EDIT:
        return (
          <AvatarImageEditor
            file={file!}
            onBack={resetStates}
            onPreview={handlePreview}
            position={position}
            scale={scale}
            setPosition={setPosition}
            setScale={setScale}
          />
        );
      case UPLOAD_STATES.CONFIRMATION:
        return (
          <>
            <Text>Any changes you've made won't be saved if you continue.</Text>
            <ChakraFlex
              alignItems="center"
              justifyContent="space-between"
              marginTop="24px"
              width="100%"
            >
              <FireflyButton
                id={`${TOP_LEVEL_ID}__left-button`}
                leftIcon={FAIconName.arrowLeftRegular}
                onClick={() => setUploadState(UPLOAD_STATES.PREVIEW)}
                variant="secondary"
              >
                Back
              </FireflyButton>
              <FireflyButton
                id={`${TOP_LEVEL_ID}__right-button`}
                leftIcon={FAIconName.trashCanRegular}
                onClick={onClose}
                variant="primary"
              >
                Discard changes
              </FireflyButton>
            </ChakraFlex>
          </>
        );
      default:
        return <InitialContent />;
    }
  };

  const getModalTitle = () => {
    switch (uploadState) {
      case UPLOAD_STATES.PREVIEW:
        return 'Preview photo';
      case UPLOAD_STATES.EDIT:
        return 'Edit photo';
      case UPLOAD_STATES.CONFIRMATION:
        return 'Are you sure?';
      default:
        return 'Upload photo';
    }
  };

  const handleClose = () => {
    if (uploadState === UPLOAD_STATES.PREVIEW) {
      setUploadState(UPLOAD_STATES.CONFIRMATION);
    } else onClose();
  };

  return (
    <ModalV2
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={handleClose}
      size={uploadState === UPLOAD_STATES.CONFIRMATION ? 'md' : '2xl'}
      title={getModalTitle()}
    >
      {renderContent()}
      <input
        accept={ACCEPTED_IMAGE_TYPES.join(', ')}
        data-testid="upload-avatar-attachment-input"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
      />
    </ModalV2>
  );
};

export default AvatarUpload;
